import React from 'react';
import type { MarketRate, MarketPortfolio } from '../../api/client';

/** Cents -> "$140" or "$140.50". Null when there's no figure. */
export const formatCents = (cents?: number | null): string | null => {
  if (cents == null) return null;
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toFixed(whole ? 0 : 2)}`;
};

/**
 * Compact secondary-market badge for one event: lowest ask (going price) and
 * highest bid (sellable now). Renders nothing when the event isn't on CrowdVolt
 * or both sides of the book are empty — so it's safe to drop next to every row.
 */
export const MarketRateBadge: React.FC<{ rate?: MarketRate | null; className?: string }> = ({
  rate,
  className,
}) => {
  if (!rate || !rate.matched) return null;
  const ask = formatCents(rate.lowest_ask_cents);
  const bid = formatCents(rate.highest_bid_cents);
  if (!ask && !bid) return null;
  const title =
    'CrowdVolt resale' +
    (ask ? ` · lowest ask ${ask}` : '') +
    (bid ? ` · highest bid ${bid}` : '') +
    (rate.last_sale ? ` · last sale ${rate.last_sale}` : '');
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-sky-300 bg-sky-500/10 whitespace-nowrap ${className || ''}`}
      title={title}
    >
      Resale{ask ? ` ask ${ask}` : ''}
      {ask && bid ? ' ·' : ''}
      {bid ? ` bid ${bid}` : ''}
    </span>
  );
};

/**
 * "Current resale value" summary for the top of the tickets list: what the held
 * tickets would fetch on the secondary market now vs what they cost. Renders
 * nothing until at least one position is priced.
 */
export const ResaleValueBanner: React.FC<{ portfolio?: MarketPortfolio | null; className?: string }> = ({
  portfolio,
  className,
}) => {
  if (!portfolio || portfolio.priced_positions === 0) return null;
  const value = formatCents(portfolio.value_cents);
  const cost = formatCents(portfolio.cost_cents);
  const pnlUp = portfolio.pnl_cents >= 0;
  const pnl = formatCents(Math.abs(portfolio.pnl_cents));
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-sky-500/20 bg-sky-500/5 px-3 py-2 text-sm ${className || ''}`}
    >
      <span className="text-gray-400">Current resale value</span>
      <span className="font-semibold text-sky-300">{value}</span>
      {cost && <span className="text-gray-500">cost {cost}</span>}
      {pnl && (
        <span className={pnlUp ? 'text-green-400' : 'text-red-400'}>
          {pnlUp ? '+' : '−'}
          {pnl}
        </span>
      )}
      {portfolio.unpriced_positions > 0 && (
        <span className="text-gray-500">({portfolio.unpriced_positions} not on resale)</span>
      )}
    </div>
  );
};
